import { Link } from 'wouter';
import { Home, UserPlus, LogIn, ArrowRight, Sparkles } from 'lucide-react';
import { UnifiedButton } from '@/components/ui/UnifiedButton';
import { UnifiedCard } from '@/components/ui/UnifiedCard';
import logoImage from '@assets/logo_1755310245094.png';

/**
 * Public 404 page for non-authenticated visitors.
 * Glass morphism design with colorful gradients matching style guide.
 */
export default function PublicNotFound() {
  const publicPaths = [
    {
      path: '/',
      label: 'Home',
      description: 'Return to the landing page',
      icon: Home,
      gradient: 'from-cyan-500 to-blue-500',
      shadowColor: 'rgba(6, 182, 212, 0.3)',
    },
    {
      path: '/sign-in',
      label: 'Sign In',
      description: 'Access your account',
      icon: LogIn,
      gradient: 'from-purple-500 to-pink-500',
      shadowColor: 'rgba(168, 85, 247, 0.3)',
    },
    {
      path: '/enroll',
      label: 'Enroll Free',
      description: 'Join the academy',
      icon: UserPlus,
      gradient: 'from-amber-500 to-orange-500',
      shadowColor: 'rgba(245, 158, 11, 0.3)',
    },
  ];

  return (
    <div className="min-h-screen bg-background text-muted-foreground overflow-hidden">
      

      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating orb top-left */}
        <div
          className="absolute -top-20 -left-20 w-80 h-80 opacity-20 blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.6) 0%, transparent 70%)',
          }}
        />
        {/* Floating orb bottom-right */}
        <div
          className="absolute -bottom-32 -right-32 w-96 h-96 opacity-20 blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.5) 0%, transparent 70%)',
          }}
        />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage:
              'linear-gradient(#06b6d4 1px, transparent 1px), linear-gradient(90deg, #06b6d4 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
      </div>

      <main className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-lg mx-auto text-center w-full">
          <UnifiedCard
            variant="elevated"
            className="relative overflow-hidden border-0"
            style={{
              background:
                'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.85) 50%, rgba(15, 23, 42, 0.95) 100%)',
              backdropFilter: 'blur(24px)',
              boxShadow:
                '0 30px 100px rgba(6, 182, 212, 0.2), 0 0 120px rgba(168, 85, 247, 0.15), inset 0 1px 0 rgba(255,255,255,0.1)',
            }}
          >
            {/* Glass shine effect */}
            <div
              className="absolute inset-0 opacity-40 pointer-events-none"
              style={{
                background:
                  'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, transparent 40%, rgba(168, 85, 247, 0.15) 100%)',
              }}
            />

            <div className="relative p-8 md:p-12">
              {/* Logo with glow */}
              <div className="flex items-center justify-center mb-8">
                <div className="relative">
                  <div className="absolute inset-0 blur-3xl bg-gradient-to-r from-cyan-500/40 to-purple-500/40 rounded-full scale-150" />
                  <img
                    src={logoImage}
                    alt="White Rabbit Academy"
                    className="relative w-28 h-auto md:w-36 object-contain drop-shadow-2xl"
                  />
                </div>
              </div>

              {/* 404 with animated gradient */}
              <div className="mb-6">
                <h1
                  className="text-8xl md:text-9xl font-black tracking-tight"
                  style={{
                    background:
                      'linear-gradient(135deg, #06b6d4 0%, #a855f7 40%, #ec4899 70%, #f59e0b 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 0 40px rgba(6, 182, 212, 0.4))',
                  }}
                >
                  404
                </h1>
              </div>

              {/* Message */}
              <h2 className="text-2xl md:text-3xl font-bold mb-3 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                It Appears You May Have Gone Down the Wrong Rabbit Hole
              </h2>

              <p className="text-muted-foreground text-base mb-8">
                The page you&apos;re seeking doesn&apos;t exist—but perhaps you were meant to find
                something better.
              </p>

              {/* Navigation Options */}
              <div className="space-y-3 mb-8">
                {publicPaths.map(item => {
                  const IconComponent = item.icon;
                  return (
                    <Link href={item.path} key={item.path}>
                      <div
                        className="group relative p-4 rounded-[1.3rem] overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                        style={{
                          background:
                            'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.8) 100%)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          boxShadow: `0 4px 24px ${item.shadowColor}`,
                        }}
                      >
                        {/* Hover glow effect */}
                        <div
                          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          style={{
                            background: `linear-gradient(135deg, ${item.shadowColor} 0%, transparent 100%)`,
                          }}
                        />

                        <div className="relative flex items-center gap-4">
                          <div
                            className={`w-12 h-12 rounded-[1rem] bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                            style={{ boxShadow: `0 0 20px ${item.shadowColor}` }}
                          >
                            <IconComponent className="w-6 h-6 text-foreground" />
                          </div>
                          <div className="flex-1 text-left">
                            <h4 className="font-semibold text-foreground group-hover:text-cyan-300 transition-colors">
                              {item.label}
                            </h4>
                            <p className="text-muted-foreground text-sm">{item.description}</p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-brite-terra group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* CTA Button */}
              <Link href="/enroll">
                <UnifiedButton
                  variant="primary"
                  size="lg"
                  className="w-full relative overflow-hidden group h-14"
                  style={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #a855f7 50%, #ec4899 100%)',
                    boxShadow:
                      '0 8px 32px rgba(6, 182, 212, 0.4), 0 0 60px rgba(168, 85, 247, 0.2)',
                  }}
                >
                  <span className="relative flex items-center justify-center gap-2 font-bold text-foreground">
                    <Sparkles className="w-5 h-5" />
                    Join White Rabbit Academy
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </UnifiedButton>
              </Link>
            </div>
          </UnifiedCard>
        </div>
      </main>
    </div>
  );
}
